'use client'

import { useState } from 'react'
import { MapPin, Maximize, CheckCircle, Droplets, Zap, Phone, Car, School, Heart, ShoppingCart, Train, Info, FileText } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import Link from 'next/link'

export function PropertyFilter({ initialProperties }: { initialProperties: any[] }) {
    const [filter, setFilter] = useState<'A Vendre' | 'A Louer'>('A Vendre')
    const [expandedIds, setExpandedIds] = useState<string[]>([])

    const filteredProperties = initialProperties
        .filter((p: any) => (p.status || 'A Vendre') === filter)
        .sort((a: any, b: any) => (b.is_premium ? 1 : 0) - (a.is_premium ? 1 : 0))

    const toggleExpand = (id: string) => {
        setExpandedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        )
    }

    return (
        <div>
            {/* Tabs */}
            <div className="flex justify-center mb-12">
                <div className="bg-secondary p-1 rounded-lg border border-white/10 inline-flex">
                    <button
                        onClick={() => setFilter('A Vendre')}
                        className={`px-8 py-3 rounded-md font-bold transition-all ${filter === 'A Vendre' ? 'bg-primary text-black shadow-lg' : 'text-gray-400 hover:text-white'
                            }`}
                    >
                        A Vendre
                    </button>
                    <button
                        onClick={() => setFilter('A Louer')}
                        className={`px-8 py-3 rounded-md font-bold transition-all ${filter === 'A Louer' ? 'bg-primary text-black shadow-lg' : 'text-gray-400 hover:text-white'
                            }`}
                    >
                        A Louer
                    </button>
                </div>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredProperties.map((property) => (
                    <div
                        key={property.id}
                        className={`bg-secondary rounded-xl overflow-hidden border transition-all group flex flex-col ${property.is_premium
                                ? 'border-yellow-500 shadow-[0_0_20px_rgba(234,179,8,0.15)] relative'
                                : 'border-white/5 hover:border-primary/50'
                            }`}
                    >
                        {property.is_premium && (
                            <div className="absolute top-0 left-1/2 -translate-x-1/2 bg-yellow-500 text-black text-[10px] font-bold px-3 py-1 rounded-b-lg shadow-lg z-20 uppercase tracking-widest flex items-center gap-1">
                                💎 Offre Premium
                            </div>
                        )}

                        {/* Image Header */}
                        <div className="h-64 bg-gray-800 relative overflow-hidden group-hover:scale-[1.02] transition-transform duration-500">
                            {property.images && property.images.length > 0 ? (
                                <img
                                    src={property.images[0]}
                                    alt={property.title}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full bg-gray-800 flex items-center justify-center text-gray-600">
                                    <span className="text-4xl text-gray-700">🏠</span>
                                </div>
                            )}

                            <div className="absolute top-2 right-2 bg-black/80 backdrop-blur px-3 py-1 rounded-full text-xs font-bold text-white z-10 border border-white/10 uppercase tracking-widest">
                                {property.type}
                            </div>
                            {property.status === 'A Louer' && (
                                <div className="absolute top-2 left-2 bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                                    LOCATION
                                </div>
                            )}
                        </div>

                        {/* Content */}
                        <div className="p-6 flex-grow flex flex-col">
                            <div className="flex justify-between items-start mb-2">
                                <h3 className="text-xl font-bold text-white group-hover:text-primary transition-colors line-clamp-2">
                                    {property.title}
                                </h3>
                                {property.verified && (
                                    <div className="flex items-center ml-2" title="Vérifié par la Mairie">
                                        <CheckCircle className="w-5 h-5 text-blue-400 shrink-0" />
                                    </div>
                                )}
                            </div>

                            <div className="text-2xl font-bold text-primary mb-4">{property.price}</div>

                            {/* Basic Info */}
                            <div className="space-y-3 mb-6">
                                <div className="flex items-center text-gray-300 text-sm">
                                    <MapPin className="w-4 h-4 mr-3 text-gray-500" />
                                    <span className="truncate">{property.location}</span>
                                </div>
                                <div className="flex items-center text-gray-300 text-sm">
                                    <Maximize className="w-4 h-4 mr-3 text-gray-500" />
                                    {property.area}
                                </div>

                                {/* Utilities Icons */}
                                <div className="flex gap-4 pt-2 border-t border-white/5 mt-3">
                                    {property.has_water && <div className="flex items-center text-xs text-blue-300" title="Eau"><Droplets size={14} className="mr-1" /> Eau</div>}
                                    {property.has_electricity && <div className="flex items-center text-xs text-yellow-300" title="Électricité"><Zap size={14} className="mr-1" /> Élec</div>}
                                    {property.has_telephone && <div className="flex items-center text-xs text-green-300" title="Tel/Internet"><Phone size={14} className="mr-1" /> Tel</div>}
                                    {property.soil_type && <div className="flex items-center text-xs text-amber-600" title="Type de sol">🌱 {property.soil_type}</div>}
                                </div>
                            </div>

                            {/* Expandable Details */}
                            {expandedIds.includes(property.id) && (
                                <div className="bg-black/20 p-4 rounded-lg mb-4 text-sm animate-in fade-in slide-in-from-top-2">
                                    {/* Legal Status (Sale only) */}
                                    {property.status !== 'A Louer' && property.legal_status && (
                                        <div className="mb-3 pb-3 border-b border-white/5">
                                            <span className="block text-xs uppercase text-gray-500 mb-1">Juridique</span>
                                            <span className="flex items-center text-white"><FileText size={14} className="mr-2 text-primary" /> {property.legal_status}</span>
                                        </div>
                                    )}

                                    {/* Description */}
                                    <p className="text-gray-400 mb-4 text-sm leading-relaxed">{property.description}</p>

                                    {/* Distances */}
                                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-400">
                                        {property.dist_school && <div className="flex items-center"><School size={12} className="mr-1" /> École: {property.dist_school}</div>}
                                        {property.dist_hospital && <div className="flex items-center"><Heart size={12} className="mr-1" /> Santé: {property.dist_hospital}</div>}
                                        {property.dist_market && <div className="flex items-center"><ShoppingCart size={12} className="mr-1" /> Marché: {property.dist_market}</div>}
                                        {property.dist_station && <div className="flex items-center"><Train size={12} className="mr-1" /> Gare: {property.dist_station}</div>}
                                        {property.dist_road && <div className="flex items-center"><Car size={12} className="mr-1" /> Route: {property.dist_road}</div>}
                                    </div>
                                </div>
                            )}

                            <button
                                onClick={() => toggleExpand(property.id)}
                                className="w-full text-center text-xs text-gray-500 hover:text-white uppercase tracking-widest font-bold py-2 mb-4 border-t border-white/5 hover:bg-white/5 transition-colors"
                            >
                                {expandedIds.includes(property.id) ? 'Masquer les détails' : 'Voir les détails'}
                            </button>

                            <div className="flex gap-2 mt-auto">
                                <a
                                    href={`tel:${property.owner_contact}`}
                                    className="flex-1 bg-primary text-black font-bold py-3 text-center rounded hover:bg-primary/90 transition-colors flex justify-center items-center gap-2"
                                >
                                    <Phone size={18} /> Appeler
                                </a>
                                {property.google_maps_url && (
                                    <a
                                        href={property.google_maps_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="px-4 bg-white/10 text-white rounded hover:bg-white/20 flex items-center justify-center transition-colors"
                                        title="Voir sur la carte"
                                    >
                                        <MapPin size={20} />
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {filteredProperties.length === 0 && (
                <div className="text-center py-20 bg-secondary rounded-xl border border-white/10 mt-8">
                    <p className="text-gray-500 text-lg">Aucune annonce disponible dans cette catégorie pour le moment.</p>
                </div>
            )}
        </div>
    )
}
